import React, { PropsWithChildren } from 'react';
import styled, { createGlobalStyle, css, /*StyleSheetManager,*/ ThemeProvider } from 'styled-components';
import { ITheme } from './theme';
import { useDefaultFontCss, useFonts } from '../fundamentals/typography/typography';
import { Z_INDICES } from '../common/constants';
import { A, Span } from '../components/Texts/Texts';
import { getAssetsBaseUrl } from '../config';
import { AppStage } from '@cmctechnology/webinvest-constants';
import { BaseDiv } from '../common/BaseElements';

export interface IThemeProviderProps {
  theme: ITheme;
  useGlobalStyles?: boolean;
}

const useBoxSizingCss = css`
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }
`;

const cssReset = css`
  body {
    ${useDefaultFontCss};
  }

  // https://css-tricks.com/almanac/properties/b/box-sizing/
  // Note: add && or &&& to override this
  ${useBoxSizingCss}
`;

export const GLOBAL_CLASSNAMES = {
  TOP_OVERLAY: `t_ol`,
  TOP_OVERLAY_TARGET_BG: `t_ol_b`
};

export const useGlobalClassesCss = css`
  ${`.${GLOBAL_CLASSNAMES.TOP_OVERLAY}`} {
    z-index: ${Z_INDICES.TOP_OVERLAY};
  }

  ${A}.${GLOBAL_CLASSNAMES.TOP_OVERLAY},
  ${Span}.${GLOBAL_CLASSNAMES.TOP_OVERLAY} {
    position: relative; // cancelling position static native in links and span
  }

  ${`.${GLOBAL_CLASSNAMES.TOP_OVERLAY_TARGET_BG}.${GLOBAL_CLASSNAMES.TOP_OVERLAY}`} {
    background: ${({ theme }) => theme.colours.misc.background};
  }
`;

const GlobalStyles = createGlobalStyle`
  ${cssReset};
  ${useGlobalClassesCss};
`;

const FontStyles = createGlobalStyle<{ baseAssetsUrl: string }>`  
  ${({ baseAssetsUrl }) => useFonts(baseAssetsUrl)};
`;

const EoraWrapper = styled(BaseDiv)`
  & > * {
    ${useDefaultFontCss}
  }
  ${useBoxSizingCss};
  ${useGlobalClassesCss};
`;

export interface IEoraAssetsProviderProps {
  baseAssetsUrl?: string;
  currentEnv?: AppStage;
}

export const EoraAssetsContext = React.createContext<IEoraAssetsProviderProps>({});

export const EoraThemeProvider: React.FC<PropsWithChildren<IThemeProviderProps & IEoraAssetsProviderProps>> = ({
  children,
  theme,
  useGlobalStyles,
  ...rest
}) => {
  const usingGlobalStyles = useGlobalStyles === false ? false : theme.hasGlobalStyles;
  const themeOverrides = { ...theme, hasGlobalStyles: usingGlobalStyles };
  const baseAssetsUrl = getAssetsBaseUrl(rest);

  if (usingGlobalStyles) {
    return (
      // <StyleSheetManager enableVendorPrefixes>
      <EoraAssetsContext.Provider value={{ baseAssetsUrl }}>
        <ThemeProvider theme={themeOverrides}>
          <GlobalStyles />
          <FontStyles baseAssetsUrl={baseAssetsUrl} />
          {children}
        </ThemeProvider>
      </EoraAssetsContext.Provider>
      // </StyleSheetManager>
    );
  }

  return (
    // <StyleSheetManager enableVendorPrefixes>
    <EoraAssetsContext.Provider value={{ baseAssetsUrl }}>
      <ThemeProvider theme={themeOverrides}>
        <EoraWrapper data-testid='eora' className='eoraDiv'>
          <FontStyles baseAssetsUrl={baseAssetsUrl} />
          {children}
        </EoraWrapper>
      </ThemeProvider>
    </EoraAssetsContext.Provider>
    // </StyleSheetManager>
  );
};
